import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronRight, Route, TrendingUp, TrendingDown } from 'lucide-react';
import { strongestWeakest, BAND_TONE } from '../../lib/leiConditions';

/**
 * Missing Middle Journey Map + Condition Propagation (Rec 3 + Rec 5).
 * Replaces the spider chart with a journey: capability conditions
 * propagate forward into value conditions. Same LEI scores, told as
 * a story with strongest / weakest condition callouts.
 */
export function ConditionPropagation({ conditions }) {
  if (!conditions?.length) return null;
  const { strongest, weakest } = strongestWeakest(conditions);

  return (
    <Card data-testid="condition-propagation">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Route className="h-4 w-4 text-cyan-700" />
          Missing Middle Journey · Condition Propagation
        </CardTitle>
        <p className="text-xs text-slate-500">
          How capability conditions propagate forward into value — the same LEI scores, read as a journey.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-stretch overflow-x-auto gap-0.5 pb-1">
          {conditions.map((c, i) => (
            <React.Fragment key={c.key}>
              {i > 0 && (
                <div className="flex items-center justify-center px-1 flex-shrink-0">
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </div>
              )}
              <div
                className="flex-1 min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-center"
                data-testid={`propagation-${c.key}`}
                style={{ borderTopColor: c.color, borderTopWidth: 3 }}
              >
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{c.label}</p>
                <p className="text-2xl font-extrabold tabular-nums mt-0.5 text-slate-900">
                  {c.score != null ? Math.round(c.score) : '—'}
                </p>
                <span className={`inline-block mt-1 rounded-full border px-2 py-0.5 text-[9px] font-bold ${BAND_TONE[c.band.tone]}`}>
                  {c.band.label}
                </span>
                <div className="h-1 mt-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, c.score ?? 0))}%`, backgroundColor: c.color }} />
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="condition-strongest">
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Strongest Condition
            </p>
            <p className="text-base font-extrabold text-emerald-900 mt-0.5">
              {strongest ? `${strongest.label} · ${Math.round(strongest.score)}` : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5" data-testid="condition-weakest">
            <p className="text-[10px] font-bold uppercase tracking-widest text-rose-700 flex items-center gap-1">
              <TrendingDown className="h-3 w-3" /> Weakest Condition
            </p>
            <p className="text-base font-extrabold text-rose-900 mt-0.5">
              {weakest ? `${weakest.label} · ${Math.round(weakest.score)}` : '—'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
